import { useEffect, useState } from "react";
import { toast } from "../../../utils/notify";
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import ApiLoader from "../../ui/ApiLoader";
import { Badge } from "../../ui";
import { DataTable } from "../../common/DataTable";
import {
  fetchDspmComplianceMasterFrameworks,
  fetchDspmComplianceMasterFrameworkControls,
} from "../../../services/axios/AuthApis/DashboardApis";

// ================= DSPM Compliance Master (PlatformAdmin) =================
// Mirrors Trust Center's own FrameworkMaster/ControlMaster catalog UI
// exactly (card grid -> breadcrumb + search + table drill-down), but
// scoped to only the DSPM-relevant Compliance Master
// (DSPMComplianceFramework/DSPMComplianceControl), not Trust Center's
// full catalog. Always shows every active framework/control - never
// filtered by customer_id, never affected by any CustomerAdmin's
// framework enablement state.
const CompliancePlatformAdmin = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [fwLoading, setFwLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [selectedFw, setSelectedFw] = useState(null);
  const [controls, setControls] = useState([]);
  const [ctrlLoading, setCtrlLoading] = useState(false);
  const [ctrlSearch, setCtrlSearch] = useState("");

  const loadFrameworks = async () => {
    setFwLoading(true);
    try {
      const d = await fetchDspmComplianceMasterFrameworks();
      setFrameworks(Array.isArray(d?.frameworks) ? d.frameworks : []);
    } catch {
      toast.error("Failed to load DSPM Compliance Master");
    } finally {
      setFwLoading(false);
    }
  };

  useEffect(() => {
    loadFrameworks();
  }, []);

  const openFramework = async (fw) => {
    setSelectedFw(fw);
    setControls([]);
    setCtrlSearch("");
    setCtrlLoading(true);
    try {
      const d = await fetchDspmComplianceMasterFrameworkControls(fw.id);
      setControls(Array.isArray(d?.controls) ? d.controls : []);
    } catch {
      toast.error("Failed to load controls");
    } finally {
      setCtrlLoading(false);
    }
  };

  const goBack = () => {
    setSelectedFw(null);
    setControls([]);
  };

  const filteredFrameworks = frameworks.filter((fw) => {
    const q = search.toLowerCase();
    return !q || fw.name.toLowerCase().includes(q) || (fw.description || "").toLowerCase().includes(q);
  });

  const filteredControls = controls.filter((c) => {
    const q = ctrlSearch.toLowerCase();
    return (
      !q ||
      c.control_id.toLowerCase().includes(q) ||
      c.title.toLowerCase().includes(q) ||
      (c.category || "").toLowerCase().includes(q)
    );
  });

  // ── Controls drill-down (mirrors Trust Center's ControlsView) ──
  if (selectedFw) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary-600"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Frameworks
          </button>
          <span className="text-gray-300 text-lg">/</span>
          <h2 className="text-lg font-semibold text-gray-900">
            {selectedFw.name}
            <span className="ml-2 text-sm font-normal text-gray-500">({controls.length} controls)</span>
          </h2>
        </div>

        <div className="mb-4 relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID, title or category…"
            value={ctrlSearch}
            onChange={(e) => setCtrlSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none"
          />
        </div>

        <DataTable
          columns={[
            {
              accessorKey: "control_id",
              header: "Control ID",
              cell: ({ getValue }) => <span className="font-mono text-xs text-gray-700">{getValue()}</span>,
            },
            {
              accessorKey: "title",
              header: "Title",
              cell: ({ row }) => (
                <div>
                  <p className="font-medium text-gray-900">{row.original.title}</p>
                  {row.original.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{row.original.description}</p>
                  )}
                </div>
              ),
            },
            {
              accessorKey: "category",
              header: "Category",
              cell: ({ getValue }) => <Badge variant="secondary" size="sm">{getValue() || "-"}</Badge>,
            },
            {
              id: "evaluation",
              header: "Evaluation",
              cell: ({ row }) =>
                row.original.has_existing_check ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700">
                    <CheckCircleIcon className="w-4 h-4" />
                    Evaluable ({row.original.mapped_check_ids.length} check{row.original.mapped_check_ids.length > 1 ? "s" : ""})
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">No DSPM check yet</span>
                ),
            },
          ]}
          data={filteredControls}
          getRowId={(c) => String(c.id)}
          loading={ctrlLoading}
          emptyMessage="No controls found"
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      </div>
    );
  }

  // ── Framework card grid (mirrors Trust Center's FrameworkTable) ──
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6" style={{ color: "var(--app-primary-color, #26256b)" }} />
            Compliance Master
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            DSPM-relevant frameworks &amp; controls only
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search frameworks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <span className="text-sm text-gray-500">{filteredFrameworks.length} frameworks</span>
      </div>

      {fwLoading ? (
        <div className="flex justify-center py-20">
          <ApiLoader label="Loading frameworks..." />
        </div>
      ) : filteredFrameworks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <DocumentTextIcon className="w-14 h-14 mb-3 opacity-40" />
          <p className="text-lg font-medium">No frameworks found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFrameworks.map((fw) => (
            <div
              key={fw.id}
              onClick={() => openFramework(fw)}
              className="relative rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-200 cursor-pointer flex flex-col"
            >
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-gray-900 leading-snug">{fw.name}</p>
                  {fw.is_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">Inactive</span>
                  )}
                </div>
                {fw.description && (
                  <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{fw.description}</p>
                )}
              </div>
              <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-1.5 text-xs text-gray-500">
                <DocumentTextIcon className="w-3.5 h-3.5" />
                {fw.controls_count} controls
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompliancePlatformAdmin;
